import { useRef, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { exportNodeAsPng } from '@/lib/exportPng';

/**
 * One template of the social kit (B3-SocialMedia): its name, its real size,
 * the preview at the right ratio and « Exporter en PNG », which renders the
 * preview at the platform's dimensions. A failed export says so and offers
 * Réessayer.
 */
export interface SocialTemplateProps {
  icon?: ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;
  title: string;
  width: number;
  height: number;
  /** File name without extension. */
  filename: string;
  gradient?: string;
  children: ReactNode;
}

export function SocialTemplate({ icon: Icon, title, width, height, filename, gradient = 'from-purple-500/25 to-fuchsia-500/25', children }: SocialTemplateProps) {
  const { t } = useTranslation(['pages', 'common']);
  const previewRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const exportPng = async () => {
    if (!previewRef.current) return;
    setExporting(true);
    try {
      await exportNodeAsPng(previewRef.current, { width, height, filename });
    } catch {
      toast.error(t('pages:socialMediaKit.exportFailed', { title }), {
        action: { label: t('common:actions.retry'), onClick: () => { void exportPng(); } },
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="relative rounded-2xl border border-white/[0.05] bg-white/[0.02] p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {Icon && (
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${gradient}`}>
              <Icon className="h-4 w-4 text-white" aria-hidden />
            </div>
          )}
          <h2 className="truncate font-semibold text-white">{title}</h2>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-white/50">{width} × {height}</span>
          <button
            type="button"
            onClick={exportPng}
            disabled={exporting}
            aria-label={t('pages:socialMediaKit.exportAria', { title })}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1.5 rounded-lg border border-white/10 px-3 text-xs font-medium text-white/80 hover:bg-white/5 disabled:opacity-60"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Download className="h-4 w-4" aria-hidden />}
            <span>{t('pages:socialMediaKit.exportPng')}</span>
          </button>
        </div>
      </div>
      <AspectRatio ratio={width / height}>
        <div ref={previewRef} className="relative h-full w-full overflow-hidden rounded-xl">
          {children}
        </div>
      </AspectRatio>
    </div>
  );
}
